import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));
  const category = categories.find((c) => c.slug === article?.category);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-950">文章未找到</h2>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="organization" />
      <SEOSchema
        type="article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: article.author,
          datePublished: article.createdAt,
          image: article.cover,
          publisher: siteInfo.name,
        }}
      />
      <SEOSchema
        type="webpage"
        data={{
          name: `${article.title} - ${siteInfo.name}`,
          description: article.excerpt,
          url: `${siteInfo.url}/article/${article.id}`,
        }}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-full transition-colors hover:bg-secondary-200 cursor-pointer whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                {article.featured && (
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full whitespace-nowrap">
                    精选
                  </span>
                )}
                {article.hot && (
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-600">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-900 whitespace-nowrap">{article.author}</span>
                </div>
                <span className="text-foreground-400">·</span>
                <span className="whitespace-nowrap">{article.createdAt}</span>
                <span className="text-foreground-400">·</span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line text-base w-4 flex items-center justify-center"></i>
                  {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
                </span>
                <span className="text-foreground-400">·</span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-chat-3-line text-base w-4 flex items-center justify-center"></i>
                  {article.commentCount} 评论
                </span>
              </div>
            </div>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 今日同人库同人作品`}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-p:text-foreground-800 prose-p:leading-relaxed prose-p:text-base md:prose-p:text-lg
                prose-strong:text-foreground-950
                prose-em:text-foreground-700
                prose-li:text-foreground-800 prose-li:leading-relaxed
                prose-ul:my-4
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-sm text-foreground-600 mr-2 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-700 text-sm rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1.5 px-5 py-2.5 bg-background-100 hover:bg-background-200 rounded-full text-sm font-medium text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-lg w-4 flex items-center justify-center"></i>
                  点赞 ({article.likeCount})
                </button>
                <button className="flex items-center gap-1.5 px-5 py-2.5 bg-background-100 hover:bg-background-200 rounded-full text-sm font-medium text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line text-lg w-4 flex items-center justify-center"></i>
                  收藏
                </button>
              </div>
              <button className="flex items-center gap-1.5 px-5 py-2.5 bg-background-100 hover:bg-background-200 rounded-full text-sm font-medium text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-share-forward-line text-lg w-4 flex items-center justify-center"></i>
                分享
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    {...ra}
                    categoryName={category?.name || ra.category}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}